import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Filter, Lock, Info } from "lucide-react";
import { TONE, absenceTone, hm } from "@/lib/taskCalendar";
import { DOW, dateOf, isoOf, addDays, weekStart, isoWeek, isWorkday, holidayOf, daysWord, monthLabel, STATUS_LABEL } from "@/lib/absences";
import { formatHours } from "@/lib/format";

// 415: Docházka dělníka podle varianty C — nahoře rozdíl proti plánu jako zůstatek, pod ním výpis dnů
// od nejnovějšího (po dnech / týdnech / měsících), Filtr se zakázkou; Kalendář je druhá záložka.

export const PCOL = ["#e0703f", "#d9a21b", "#2a8467", "#64748b", "#2e8fb8", "#8b5cf6", "#c2417a"];
export const GROUPS = [["den", "Dny"], ["tyden", "Týdny"], ["mesic", "Měsíce"]];
const MON3 = ["led", "úno", "bře", "dub", "kvě", "čvn", "čvc", "srp", "zář", "říj", "lis", "pro"];
const hrs = (h) => formatHours(Math.round((h || 0) * 100) / 100);
export const signedH = (h) => `${h > 0.05 ? "+" : h < -0.05 ? "−" : ""}${hrs(Math.abs(h || 0))}`;
const dayOfRow = (r) => isoOf(new Date(r.check_in));

// Zakázky měsíce z docházky — barva podle pořadí (nejvíc hodin první)
export function projectsOf(rows) {
  const m = {};
  rows.forEach((r) => { const k = r.project_id || "—"; (m[k] = m[k] || { id: k, name: r.project || "Bez zakázky", hours: 0 }).hours += r.hours || 0; });
  return Object.values(m).sort((a, b) => b.hours - a.hours).map((p, i) => ({ ...p, color: PCOL[i % PCOL.length] }));
}

function Mini({ v, label, bad, onClick, on, testid }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} data-testid={testid}
      className={`rounded-[14px] border bg-card px-3 py-2 text-left min-w-0 ${on ? "border-destructive ring-2 ring-destructive/20" : "border-border"} ${onClick ? "hover:bg-secondary/60" : ""}`}>
      <b className={`block text-[16px] md:text-[17px] tabular-nums truncate ${bad ? "text-destructive" : ""}`}>{v}</b>
      <span className="block text-[11.5px] leading-tight text-muted-foreground">{label}</span>
    </Tag>
  );
}

// Nahoře: rozdíl proti plánu (u běžícího měsíce do dneška), odpracováno z kolika, tři dlaždice.
export function MonthHero({ me, rows, anchor, today, pf, projects, hi, onHi, onClearFilter }) {
  const [info, setInfo] = useState(false);
  const t = me?.totals, td = me?.to_date;
  const workDays = new Set(rows.map(dayOfRow)).size;
  const label = monthLabel(anchor);
  if (pf) {
    const p = projects.find((x) => x.id === pf);
    const pr = rows.filter((r) => (r.project_id || "—") === pf);
    return (
      <section className="rounded-[22px] border border-border bg-card p-4 md:p-5 flex items-center gap-3" data-testid="att-hero">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-muted-foreground truncate">{label} · na zakázce {p?.name || "—"}</div>
          <div className="text-[30px] md:text-[36px] font-extrabold tracking-tight tabular-nums leading-tight" data-testid="att-hero-project">{hrs(pr.reduce((s, r) => s + (r.hours || 0), 0))}</div>
          <div className="text-[13px] text-muted-foreground">{daysWord(new Set(pr.map(dayOfRow)).size)} · z {hrs(rows.reduce((s, r) => s + (r.hours || 0), 0))} za celý měsíc</div>
        </div>
        <button type="button" onClick={onClearFilter} className="text-sm font-bold text-brand shrink-0" data-testid="att-hero-clear">Zrušit filtr</button>
      </section>
    );
  }
  if (!t || t.due == null) {     // DPP, IČO, bez fondu — jen odpracováno
    return (
      <section className="rounded-[22px] border border-border bg-card p-4 md:p-5" data-testid="att-hero">
        <div className="text-[13px] font-semibold text-muted-foreground">Odpracováno · {label}</div>
        <div className="text-[34px] md:text-[40px] font-extrabold tracking-tight tabular-nums leading-tight">{hrs(rows.reduce((s, r) => s + (r.hours || 0), 0))}</div>
        <div className="text-[13px] text-muted-foreground">{daysWord(workDays)} v práci</div>
      </section>
    );
  }
  const cur = !!td;
  const yday = cur && td.through !== today;        // dnešní směna ještě běží / nezačala — počítá se do včerejška
  const due = cur ? td.due : t.due, worked = cur ? td.worked : t.worked, diff = cur ? td.diff : t.diff;
  const miss = (cur ? td.missing : t.missing) || 0;
  const off = (t.holiday_days || 0) + ["D", "N", "L", "NV", "NP", "makeup"].reduce((s, k) => s + (t[k] || 0), 0);
  const pct = due ? Math.min(worked / due, 1) * 100 : 0;
  return (
    <section className="rounded-[22px] border border-border p-4 md:p-5 bg-gradient-to-br from-brand/10 via-card to-card" data-testid="att-hero">
      <div className="flex items-center gap-2">
        <span className="flex-1 min-w-0 text-[13px] font-semibold text-muted-foreground leading-snug">{cur ? `${label} · rozdíl proti plánu ${yday ? "do včerejška" : "do dneška"}` : `${label} · rozdíl proti plánu`}</span>
        <button type="button" onClick={() => setInfo((v) => !v)} aria-label="Jak se to počítá" aria-expanded={info} data-testid="att-hero-info"
          className="w-7 h-7 rounded-full border border-border bg-card text-muted-foreground grid place-items-center shrink-0"><Info size={14} /></button>
      </div>
      <div className={`text-[38px] md:text-[46px] font-extrabold tracking-tight tabular-nums leading-[1.1] mt-0.5 ${diff < -0.05 ? "text-destructive" : "text-success"}`} data-testid="att-hero-diff">{signedH(diff)}</div>
      <div className="text-sm mt-0.5">Odpracováno <b className="tabular-nums" data-testid="att-hero-worked">{hrs(worked)}</b>{" "}
        <span className="text-muted-foreground">z {hrs(due)}{cur ? ` ${yday ? "do včerejška" : "do dneška"} · celý měsíc ${hrs(t.due)}` : ", které máš odpracovat"}</span></div>
      <div className="h-2.5 rounded-full bg-secondary overflow-hidden mt-3" aria-hidden="true"><div className="h-full rounded-full bg-success" style={{ width: `${pct}%` }} /></div>
      {info && (
        <div className="mt-3 rounded-[12px] bg-secondary px-3 py-2.5 text-[13px] leading-snug" data-testid="att-hero-explain">
          Měsíc má {daysWord(t.days)} × {hrs(me.target)} = <b>{hrs(t.fund)}</b>. Svátky a schválené volno se odečítají ({daysWord(off)}), takže máš odpracovat <b>{hrs(t.due)}</b>.
          {" "}Počítají se i směny, které ještě čekají na schválení. Neplacené volno, které si napracuješ, plán nesnižuje.
        </div>
      )}
      <div className="grid grid-cols-3 gap-2 mt-3">
        <Mini v={daysWord(workDays)} label="v práci" testid="att-hero-days" />
        <Mini v={daysWord(off)} label="svátek a volno" testid="att-hero-off" />
        <Mini v={daysWord(miss)} label="bez záznamu" bad={miss > 0} on={hi} onClick={miss ? onHi : undefined} testid="att-hero-missing" />
      </div>
    </section>
  );
}

function FilterBody({ projects, total, pf, setPf, group, setGroup, canGroup, onDone }) {
  return (
    <div className="space-y-1" data-testid="att-filter-body">
      <div className="text-[11.5px] font-bold uppercase tracking-[.07em] text-muted-foreground px-2 pt-1">Zakázka</div>
      {[{ id: null, name: "Všechny zakázky", hours: total }, ...projects].map((p) => (
        <button key={p.id || "all"} type="button" onClick={() => setPf(p.id)} data-testid={`att-f-${p.id || "all"}`}
          className={`w-full min-h-[42px] px-2 rounded-[10px] flex items-center gap-2.5 text-left text-sm ${pf === p.id ? "bg-secondary font-semibold" : "hover:bg-secondary/60"}`}>
          <span className={`w-[18px] h-[18px] rounded-full border-2 grid place-items-center shrink-0 ${pf === p.id ? "border-brand" : "border-border"}`}>{pf === p.id && <span className="w-2 h-2 rounded-full bg-brand" />}</span>
          {p.color && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: p.color }} />}
          <span className="flex-1 min-w-0 truncate">{p.name}</span>
          <span className="tabular-nums text-muted-foreground shrink-0">{hrs(p.hours)}</span>
        </button>
      ))}
      <div className="border-t border-border !my-2" />
      <div className="flex items-center justify-between gap-2 px-2">
        <span className="text-[11.5px] font-bold uppercase tracking-[.07em] text-muted-foreground">Výpis po</span>
        {!canGroup && <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-muted-foreground" data-testid="att-group-locked"><Lock size={12} /> jen ve Výpisu</span>}
      </div>
      <div className={`flex p-1 rounded-full bg-secondary gap-1 mx-1 ${canGroup ? "" : "opacity-45"}`}>
        {GROUPS.map(([k, l]) => (
          <button key={k} type="button" disabled={!canGroup} onClick={() => setGroup(k)} data-testid={`att-g-${k}`}
            className={`flex-1 h-9 rounded-full text-[13px] font-bold disabled:cursor-not-allowed ${group === k ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
      <div className="flex items-center justify-between gap-2 pt-2 px-1">
        <button type="button" onClick={() => { setPf(null); setGroup("tyden"); }} className="text-sm font-bold text-brand h-9" data-testid="att-f-reset">Zrušit filtr</button>
        <Button type="button" variant="outline" size="sm" onClick={onDone} className="rounded-full" data-testid="att-f-done">Hotovo</Button>
      </div>
    </div>
  );
}

// Záložky Výpis / Kalendář a tlačítko Filtr (na telefonu okno zespodu, na PC bublina).
export function StatementBar({ view, setView, mobile, projects, total, pf, setPf, group, setGroup }) {
  const [open, setOpen] = useState(false);
  const canGroup = view === "vypis";
  const extra = [pf ? projects.find((p) => p.id === pf)?.name : null, canGroup && group !== "tyden" ? GROUPS.find((g) => g[0] === group)[1] : null].filter(Boolean);
  const body = <FilterBody projects={projects} total={total} pf={pf} setPf={setPf} group={group} setGroup={setGroup} canGroup={canGroup} onDone={() => setOpen(false)} />;
  const btn = (
    <button type="button" onClick={mobile ? () => setOpen(true) : undefined} data-testid="att-filter-btn"
      className={`h-10 px-4 rounded-full border text-sm font-bold inline-flex items-center gap-2 max-w-full min-w-0 ${pf ? "bg-foreground text-background border-foreground" : "bg-card border-border hover:bg-secondary"}`}>
      <Filter size={15} className="shrink-0" /><span className="truncate">Filtr{extra.length ? ` · ${extra.join(" · ")}` : ""}</span>
    </button>
  );
  return (
    <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="att-bar">
      <div role="tablist" className="inline-flex p-1 rounded-full bg-card border border-border" aria-label="Zobrazení docházky">
        {[["vypis", "Výpis"], ["kal", "Kalendář"]].map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={view === k} onClick={() => setView(k)} data-testid={`att-view-${k}`}
            className={`h-9 px-4 rounded-full text-[13.5px] font-bold ${view === k ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>
      {mobile ? (
        <>
          {btn}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent sheet className="md:max-w-sm" data-testid="att-filter-sheet">
              <DialogHeader><DialogTitle>Filtr</DialogTitle><DialogDescription className="sr-only">Zakázka a výpis po dnech, týdnech nebo měsících</DialogDescription></DialogHeader>
              {body}
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>{btn}</PopoverTrigger>
          <PopoverContent align="end" className="w-[320px] p-2 rounded-[16px]" data-testid="att-filter-pop">{body}</PopoverContent>
        </Popover>
      )}
    </div>
  );
}

// Řádek výpisu — jako pohyb na účtu: den, ikona, co to bylo, kolik hodin.
function Row({ iso, icon, iconStyle, title, sub, amount, small, sel, hi, onPick, testid, titleClass = "", amountClass = "" }) {
  const d = dateOf(iso);
  return (
    <button type="button" onClick={() => onPick(iso)} data-testid={testid}
      className={`w-full flex items-center gap-3 px-3 md:px-3.5 py-2.5 text-left border-t border-border first:border-t-0 ${hi ? "bg-destructive/10" : sel ? "bg-secondary" : "hover:bg-secondary/50"}`}>
      <span className="w-9 shrink-0 text-center leading-tight"><b className="block text-[17px] tabular-nums">{d.getDate()}</b><span className="text-[11px] font-semibold text-muted-foreground">{DOW[(d.getDay() + 6) % 7]}</span></span>
      <span className="w-[34px] h-[34px] rounded-[11px] grid place-items-center text-[12px] font-extrabold shrink-0" style={iconStyle}>{icon}</span>
      <span className="flex-1 min-w-0"><b className={`block text-[14.5px] truncate ${titleClass}`}>{title}</b><span className="block text-[12.5px] text-muted-foreground truncate">{sub}</span></span>
      <span className={`text-right shrink-0 tabular-nums font-extrabold whitespace-nowrap ${amountClass}`}>{amount}{small && <small className="block text-[11.5px] font-semibold text-muted-foreground">{small}</small>}</span>
    </button>
  );
}

// Dny měsíce jako řádky výpisu (od nejnovějšího): směny, volno, svátek, bez záznamu. Budoucí dny jen s volnem.
export function buildEntries({ from, to, today, rows, onDay, cells, pf }) {
  const byDay = {};
  rows.forEach((r) => { if (!pf || (r.project_id || "—") === pf) (byDay[dayOfRow(r)] = byDay[dayOfRow(r)] || []).push(r); });
  const out = [];
  for (let iso = to; iso >= from; iso = addDays(iso, -1)) {
    const future = iso > today;
    const list = [];
    (byDay[iso] || []).forEach((r) => list.push({ kind: "shift", r }));
    if (!pf) {
      const a = onDay(iso);
      if (a) list.push({ kind: "abs", a });
      else if (!future && !(byDay[iso] || []).length) {
        const w = dateOf(iso).getDay();
        if (holidayOf(iso) && w !== 0 && w !== 6) list.push({ kind: "hol" });
        else if (cells?.[iso]?.missing) list.push({ kind: "miss" });
      }
    }
    if (future && !list.some((e) => e.kind === "abs")) continue;
    list.forEach((e) => out.push({ ...e, iso }));
  }
  return out;
}

export function Statement({ entries, group, year, anchor, today, target, projects, sel, hi, onPick, onMonth }) {
  const pcol = (id) => projects.find((p) => p.id === (id || "—"))?.color || PCOL[0];
  const row = (e, i) => {
    const key = `${e.iso}-${e.kind}-${i}`;
    if (e.kind === "shift") {
      const r = e.r, c = pcol(r.project_id);
      const st = !r.check_out ? "v práci" : r.approval_status === "approved" ? "schváleno" : "čeká na schválení";
      return <Row key={key} iso={e.iso} sel={sel === e.iso} onPick={onPick} testid={`att-row-${e.iso}`}
        icon="●" iconStyle={{ background: `${c}22`, color: c }} title={r.project || "Bez zakázky"}
        sub={`${hm(new Date(r.check_in))}–${r.check_out ? hm(new Date(r.check_out)) : "teď"}${r.break_minutes ? ` · pauza ${r.break_minutes} min` : ""} · ${st}`}
        amount={r.check_out ? hrs(r.hours) : "teď"} small={r.check_out && r.approval_status !== "approved" ? "čeká" : null} />;
    }
    if (e.kind === "abs") {
      const a = e.a, tone = absenceTone(a);
      return <Row key={key} iso={e.iso} sel={sel === e.iso} onPick={onPick} testid={`att-row-${e.iso}`}
        icon={a.code} iconStyle={{ background: TONE[tone].hatch, color: TONE[tone].fg }} title={`${a.type_label}${a.makeup ? " · napracuji" : ""}`}
        sub={`${a.part === "half" ? "půl dne" : a.part === "time" ? `${a.t_from}–${a.t_to}` : "celý den"} · ${STATUS_LABEL[a.status] || a.status}`}
        amount={<span className="text-muted-foreground">—</span>} small={a.makeup ? "v plánu" : "mimo plán"} />;
    }
    if (e.kind === "hol") {
      return <Row key={key} iso={e.iso} sel={sel === e.iso} onPick={onPick} testid={`att-row-${e.iso}`}
        icon="S" iconStyle={{ background: "hsl(var(--success) / .14)", color: "hsl(var(--success))" }} title="Svátek" sub={holidayOf(e.iso)}
        amount={<span className="text-muted-foreground">—</span>} small="mimo plán" />;
    }
    return <Row key={key} iso={e.iso} sel={sel === e.iso} hi={hi} onPick={onPick} testid={`att-row-${e.iso}`}
      icon="?" iconStyle={{ background: "hsl(var(--destructive) / .1)", color: "hsl(var(--destructive))", border: "1px dashed hsl(var(--destructive))" }}
      title="Bez záznamu" titleClass="text-destructive" sub="žádná směna ani volno"
      amount="0 h" amountClass="text-destructive" small={target ? `plán ${hrs(target)}` : null} />;
  };
  const head = (left, right) => (
    <div className="flex items-center justify-between gap-2 px-1 pt-3 pb-1.5 text-[12px] font-bold tracking-[.04em] uppercase text-muted-foreground">
      <span className="truncate">{left}</span><span className="tabular-nums shrink-0 normal-case">{right}</span>
    </div>
  );
  const box = (children) => <div className="bg-card border border-border rounded-[18px] overflow-hidden">{children}</div>;
  const shiftH = (list) => list.reduce((s, e) => s + (e.kind === "shift" ? e.r.hours || 0 : 0), 0);

  if (group === "mesic") {
    if (!year) return <div className="h-40 rounded-[18px] bg-secondary animate-pulse motion-reduce:animate-none" />;
    const months = [...(year.months || [])].reverse();
    if (!months.length) return <p className="text-sm text-muted-foreground bg-card border border-border rounded-[18px] p-4">V tomto roce zatím žádná docházka.</p>;
    return (
      <div data-testid="att-statement-months">
        {head(`Rok ${year.year} · po měsících`, hrs(months.reduce((s, m) => s + (m.worked || 0), 0)))}
        {box(months.map((m) => {
          const d = dateOf(`${m.month}-01`);
          return (
            <button key={m.month} type="button" onClick={() => onMonth(`${m.month}-01`)} data-testid={`att-month-${m.month}`}
              className={`w-full flex items-center gap-3 px-3.5 py-3 text-left border-t border-border first:border-t-0 hover:bg-secondary/50 ${m.month === anchor.slice(0, 7) ? "bg-secondary" : ""}`}>
              <span className="w-11 shrink-0 text-center leading-tight"><b className="block text-[15px]">{MON3[d.getMonth()]}</b><span className="text-[11px] font-semibold text-muted-foreground">{d.getFullYear()}</span></span>
              <span className="flex-1 min-w-0"><b className="block text-[14.5px] truncate">{monthLabel(`${m.month}-01`)}{m.current ? (m.through && m.through !== today ? " · do včerejška" : " · do dneška") : ""}</b>
                <span className="block text-[12.5px] text-muted-foreground truncate">{m.due != null ? `plán ${hrs(m.due)}` : "bez plánu"}{m.missing ? ` · ${daysWord(m.missing)} bez záznamu` : ""}</span></span>
              <span className="text-right shrink-0 tabular-nums font-extrabold whitespace-nowrap">{hrs(m.worked)}
                {m.diff != null && <small className={`block text-[11.5px] font-bold ${m.diff < -0.05 ? "text-destructive" : "text-success"}`}>{signedH(m.diff)}</small>}</span>
            </button>
          );
        }))}
      </div>
    );
  }
  if (!entries.length) return <p className="text-sm text-muted-foreground bg-card border border-border rounded-[18px] p-4" data-testid="att-statement-empty">V tomto měsíci zatím nic.</p>;
  if (group === "den") {
    return <div data-testid="att-statement-days">{head(`${monthLabel(anchor)} · po dnech`, hrs(shiftH(entries)))}{box(entries.map(row))}</div>;
  }
  // po týdnech: hlavička s hodinami a plánem týdne (pracovní dny bez svátku a volna, u běžícího měsíce do dneška)
  const weeks = [];
  entries.forEach((e) => { const k = weekStart(e.iso); const w = weeks[weeks.length - 1]; if (w && w.k === k) w.list.push(e); else weeks.push({ k, list: [e] }); });
  const m0 = anchor.slice(0, 7);
  return (
    <div data-testid="att-statement-weeks">
      {weeks.map(({ k, list }) => {
        let plan = 0, a = null, b = null;
        for (let i = 0; i < 7; i++) {
          const iso = addDays(k, i);
          if (iso.slice(0, 7) !== m0) continue;
          if (dateOf(iso).getDay() % 6 !== 0) { a = a || iso; b = iso; }
          const todayOpen = iso === today && !list.some((e) => e.iso === today && e.kind === "shift" && e.r.check_out);   // dnešek až po směně
          if (!target || iso > today || todayOpen || !isWorkday(iso)) continue;
          const ab = list.find((e) => e.iso === iso && e.kind === "abs" && e.a.status === "approved" && !e.a.makeup)?.a;
          plan += ab ? (ab.part === "full" ? 0 : target / 2) : target;
        }
        const da = dateOf(a || k), db = dateOf(b || k);
        return (
          <div key={k} data-testid={`att-week-${k}`}>
            {head(`Týden ${isoWeek(k).week} · ${da.getDate()}.–${db.getDate()}. ${db.getMonth() + 1}.`, `${hrs(shiftH(list))}${target ? ` z ${hrs(plan)}` : ""}`)}
            {box(list.map(row))}
          </div>
        );
      })}
    </div>
  );
}

// Malý měsíc vedle výpisu (PC): hodiny, volno, svátek, bez záznamu; klik = den.
export function MiniMonth({ from, to, today, rows, onDay, cells, sel, hi, onPick, pf }) {
  const lead = (dateOf(from).getDay() + 6) % 7;
  const n = Math.ceil((lead + dateOf(to).getDate()) / 7) * 7;
  const byDay = {};
  rows.forEach((r) => { if (!pf || (r.project_id || "—") === pf) byDay[dayOfRow(r)] = (byDay[dayOfRow(r)] || 0) + (r.hours || 0); });
  return (
    <div className="bg-card border border-border rounded-[18px] p-3" data-testid="att-minimonth">
      <div className="text-[15px] font-bold mb-2">Kalendář</div>
      <div className="grid grid-cols-7 gap-1">
        {DOW.map((x) => <div key={x} className="text-center text-[10.5px] font-bold tracking-[.08em] text-muted-foreground py-0.5">{x}</div>)}
        {Array.from({ length: n }, (_, i) => {
          const iso = addDays(from, i - lead);
          if (iso < from || iso > to) return <div key={iso} />;
          const a = !pf && onDay(iso), h = byDay[iso], miss = !pf && cells?.[iso]?.missing, w = dateOf(iso).getDay();
          const off = w === 0 || w === 6 || holidayOf(iso);
          return (
            <button key={iso} type="button" onClick={() => onPick(iso)} data-testid={`att-mini-${iso}`}
              style={a ? { background: TONE[absenceTone(a)].hatch } : undefined}
              className={`h-11 rounded-[9px] border px-1 pt-0.5 flex flex-col items-start text-left min-w-0
                ${sel === iso ? "border-brand ring-2 ring-brand/25" : miss ? "border-dashed border-destructive" : "border-border"}
                ${miss && hi ? "bg-destructive/10" : !a && off ? "bg-secondary/60" : ""}`}>
              <span className={`text-[11px] font-bold ${iso === today ? "text-brand" : "text-muted-foreground"}`}>{dateOf(iso).getDate()}</span>
              <span className={`text-[10.5px] font-extrabold tabular-nums leading-none mt-auto mb-1 ${miss ? "text-destructive" : h ? "text-success" : ""}`}>
                {h ? String(Math.round(h * 10) / 10).replace(".", ",") : a ? a.code : miss ? "?" : holidayOf(iso) && w !== 0 && w !== 6 ? "S" : ""}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ByProject({ projects, pf, setPf }) {
  if (!projects.length) return null;
  const max = Math.max(...projects.map((p) => p.hours), 1);
  return (
    <div className="bg-card border border-border rounded-[18px] p-3.5" data-testid="att-byproject">
      <div className="flex items-baseline justify-between gap-2 mb-2"><span className="text-[15px] font-bold">Podle zakázek</span><span className="text-[12px] text-muted-foreground">klepni = jen ta zakázka</span></div>
      <div className="space-y-2.5">
        {projects.map((p) => (
          <button key={p.id} type="button" onClick={() => setPf(pf === p.id ? null : p.id)} data-testid={`att-bp-${p.id}`}
            className={`w-full text-left grid gap-1 rounded-[8px] ${pf && pf !== p.id ? "opacity-45" : ""}`}>
            <span className="flex items-center justify-between gap-2 text-[13.5px] font-semibold"><span className="truncate">{p.name}</span><span className="tabular-nums shrink-0">{hrs(p.hours)}</span></span>
            <span className="h-2.5 rounded-full bg-secondary overflow-hidden"><span className="block h-full rounded-full" style={{ width: `${(p.hours / max) * 100}%`, background: p.color }} /></span>
          </button>
        ))}
      </div>
    </div>
  );
}
